import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtMins, fmtDur, fmtTime, todayNZ, fmtDMY, STATUS_DESC, isoToNZInput, nzInputToISO } from "@/lib/format";
import { DatePicker } from "@/components/DatePicker";
import { StayDialog } from "@/components/StayDialog";
import { StayCsvDecisionsDialog } from "@/components/StayCsvDecisionsDialog";
import { HolidayVolunteerDialog, HolidayRequestStatus } from "@/components/HolidayVolunteer";
import { StayTaskChip } from "@/components/StayTaskChip";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { RosterUnseenNudge } from "@/components/RosterUnseenNudge";
import { UpcomingHolidays } from "@/components/UpcomingHolidays";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload,
  Clock,
  CheckCircle2,
  CircleDashed,
  Loader2,
  Users,
  RefreshCw,
  AlertTriangle,
  Lock,
  ChevronDown,
  DoorClosed,
  DoorOpen,
  BedDouble,
  MoonStar,
  Star,
  Plus,
  X,
  CalendarClock,
  Trash2,
  Sparkles,
  CalendarRange,
  Megaphone,
  RotateCcw,
  ListChecks,
  KeyRound,
  LogOut,
  HandHelping,
  User,
  Pencil,
  MoreHorizontal,
  ArrowUpDown,
} from "lucide-react";

const SESSION_META = {
  pending: { label: "Not started", cls: "text-muted-foreground", Icon: CircleDashed },
  in_progress: { label: "Cleaning", cls: "text-blue-600", Icon: Loader2 },
  paused: { label: "On break", cls: "text-amber-600", Icon: Clock },
  completed: { label: "Completed", cls: "text-emerald-600", Icon: CheckCircle2 },
};

// mirrors backend cleaner_is_off: days_on overrides, then dated days_off, then weekly_off
const ABSENCE_LABELS = ["Sick leave", "Sick leave (half day)", "Annual leave", "Annual leave (half day)", "Bereavement leave", "Family violence leave",
  "Unpaid leave", "Time off in lieu", "Alternative holiday", "Training / course", "Jury service", "ACC / injury", "Not rostered"];



export default function Dashboard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [date, setDate] = useState(todayNZ());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [replaceAck, setReplaceAck] = useState(false);
  const [filter, setFilter] = useState("all"); // all | assigned | unassigned | strip | completed
  const [compact, setCompact] = useState(() => {
    try { return localStorage.getItem("kleana_compact") === "1"; } catch { return false; }
  });
  const toggleCompact = (v) => {
    setCompact(v);
    try { localStorage.setItem("kleana_compact", v ? "1" : "0"); } catch { /* ignore */ }
  };
  const [sortBy, setSortBy] = useState(() => {
    try { return localStorage.getItem("kleana_sort") || "room"; } catch { return "room"; }
  });
  const changeSort = (v) => {
    setSortBy(v);
    try { localStorage.setItem("kleana_sort", v); } catch { /* ignore */ }
  };
  const [heldStrip, setHeldStrip] = useState({}); // recently-stripped rooms held for 10s undo
  const stripTimers = useRef({});
  const [, forceTick] = useState(0);
  const movedCompletedRef = useRef({}); // clean ids that have settled into the Completed tab
  const completedTimers = useRef({});
  const seenCompletedRef = useRef(null);
  const fileRef = useRef(null);

  const { data: cleans = [], isLoading } = useQuery({
    queryKey: ["cleans", date],
    queryFn: async () => (await api.get(`/cleans?date=${date}`)).data,
    refetchInterval: 15000,
  });

  const { data: autoCfg } = useQuery({
    queryKey: ["auto-key-return-config"],
    queryFn: async () => (await api.get("/auto-key-return/config")).data,
  });
  const defaultCheckout = autoCfg?.time || "10:00";

  const cleanNetMin = (c) => {
    const s = c.session;
    if (!s?.started_at || !s?.ended_at) return null;
    let sec = (new Date(s.ended_at) - new Date(s.started_at)) / 1000;
    (s.breaks || []).forEach((b) => { if (b.start && b.end) sec -= (new Date(b.end) - new Date(b.start)) / 1000; });
    return Math.max(0, sec / 60);
  };

  const { data: cleaners = [] } = useQuery({
    queryKey: ["cleaners"],
    queryFn: async () => (await api.get("/cleaners")).data,
  });

  const { data: managers = [] } = useQuery({
    queryKey: ["managers"],
    queryFn: async () => (await api.get("/managers")).data,
  });
  const [fixStrip, setFixStrip] = useState(null); // clean whose strip attribution is being corrected

  const fixStripMut = useMutation({
    mutationFn: ({ cleanId, byId }) => api.post(`/cleans/${cleanId}/strip-attribution`, { by_id: byId }),
    onSuccess: () => {
      toast.success(t("Stripped-by updated"));
      setFixStrip(null);
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const dayRosterMut = useMutation({
    mutationFn: ({ cleanerId, on }) => api.post(`/cleaners/${cleanerId}/day-roster`, { date, on }),
    onSuccess: (_r, v) => {
      toast.success(v.on ? t("Added to today's shift") : t("Removed from today"));
      qc.invalidateQueries({ queryKey: ["cleaners"] });
      qc.invalidateQueries({ queryKey: ["cleans", date] });
      qc.invalidateQueries({ queryKey: ["roster-day"] });
      qc.invalidateQueries({ queryKey: ["timeline"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const activeCleaners = cleaners.filter((c) => c.active !== false);
  const { data: rosterDay } = useQuery({
    queryKey: ["roster-day", date],
    queryFn: async () => (await api.get(`/roster/day?date=${date}`)).data,
  });
  const byId = Object.fromEntries(activeCleaners.map((c) => [c.id, c]));
  const withDoc = (r) => ({ ...(byId[r.id] || {}), ...r });
  const absentToday = (rosterDay?.off || []).filter((r) => r.kind === "absence").map(withDoc);
  const leaveToday = (rosterDay?.off || []).filter((r) => r.kind === "leave").map(withDoc);
  const rosteredOffToday = (rosterDay?.off || []).filter((r) => r.kind === "roster" || r.kind === "holiday").map(withDoc);
  const onToday = (rosterDay?.on || []).map(withDoc);
  const addedToday = onToday.filter((r) => r.added);
  const holidayToday = rosterDay?.holiday || null;
  const [askVolunteers, setAskVolunteers] = useState(false);
  const { data: dayLabels = {} } = useQuery({
    queryKey: ["roster-day-labels", date],
    queryFn: async () => (await api.get(`/roster/day-labels?date=${date}`)).data,
  });
  const dayAbsenceMut = useMutation({
    mutationFn: ({ cleanerId, label }) => api.post(`/cleaners/${cleanerId}/day-absence`, { date, label }),
    onSuccess: (_r, v) => {
      toast.success(v.label ? t("Marked {{label}} for today", { label: t(v.label) }) : t("Back on today's shift"));
      qc.invalidateQueries({ queryKey: ["cleaners"] });
      qc.invalidateQueries({ queryKey: ["cleans", date] });
      qc.invalidateQueries({ queryKey: ["timeline"] });
      qc.invalidateQueries({ queryKey: ["roster-day-labels"] });
      qc.invalidateQueries({ queryKey: ["roster-day"] });
      qc.invalidateQueries({ queryKey: ["timesheets"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const assign = useMutation({
    mutationFn: ({ cleanId, cleanerIds }) =>
      api.post(`/cleans/${cleanId}/assign`, { cleaner_ids: cleanerIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const [specialClean, setSpecialClean] = useState(null);
  const { data: specialLib = [] } = useQuery({
    queryKey: ["special-tasks"],
    queryFn: async () => (await api.get("/special-tasks")).data,
  });
  const addSpecial = useMutation({
    mutationFn: ({ cleanId, name, minutes }) =>
      api.post(`/cleans/${cleanId}/special`, { name, minutes }),
    onSuccess: () => {
      toast.success(t("Special attention added"));
      qc.invalidateQueries({ queryKey: ["cleans", date] });
      qc.invalidateQueries({ queryKey: ["special-tasks"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const removeSpecial = useMutation({
    mutationFn: ({ cleanId, itemId }) => api.delete(`/cleans/${cleanId}/special/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cleans", date] }),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const changeStatus = useMutation({
    mutationFn: ({ cleanId, status }) => api.post(`/cleans/${cleanId}/status`, { status }),
    onSuccess: (_r, v) => {
      toast.success(v.status === "departing" ? t("Room set to Departing") : t("Room set to In-house"));
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const undoDndMut = useMutation({
    mutationFn: (cleanId) => api.post(`/cleans/${cleanId}/dnd/undo`),
    onSuccess: () => {
      toast.success(t("Do Not Disturb undone — room is back on the board"));
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const setState = useMutation({
    mutationFn: ({ roomNumber, state }) =>
      api.post(`/cleans/set-state`, { room_number: roomNumber, date, state }),
    onSuccess: (_r, v) => {
      const labels = { departing: t("Departing"), in_house: t("In-house"), due: t("Due · Priority"), none: t("No clean") };
      toast.success(t("Room {{room}} → {{state}}", { room: v.roomNumber, state: labels[v.state] }));
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const [earlyCheckout, setEarlyCheckout] = useState(null); // the serviced clean pending an early-checkout re-clean
  const earlyCheckoutMut = useMutation({
    mutationFn: (cleanId) => api.post(`/cleans/${cleanId}/early-checkout`),
    onSuccess: (_r, _cleanId) => {
      toast.success(t("Early Checkout clean added"));
      setEarlyCheckout(null);
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const removeEarlyCheckoutMut = useMutation({
    mutationFn: (cleanId) => api.delete(`/cleans/${cleanId}/early-checkout`),
    onSuccess: () => {
      toast.success(t("Early Checkout clean removed"));
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const setStrip = useMutation({
    mutationFn: ({ cleanId, done }) => api.post(`/cleans/${cleanId}/strip`, { done }),
    onSuccess: (_r, v) => {
      toast.success(v.done ? t("Room marked stripped") : t("Strip undone"));
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const setKeyReturn = useMutation({
    mutationFn: ({ cleanId, returned }) => api.post(`/cleans/${cleanId}/key-return`, { returned }),
    onSuccess: (_r, v) => {
      toast.success(v.returned ? t("Key returned — ready to strip") : t("Key return undone"));
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const setCheckout = useMutation({
    mutationFn: ({ cleanId, time }) => api.post(`/cleans/${cleanId}/checkout-time`, { time }),
    onSuccess: () => {
      toast.success(t("Checkout time updated"));
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  // reset the Completed-tab hold whenever the day changes
  useEffect(() => {
    Object.values(completedTimers.current).forEach(clearTimeout);
    completedTimers.current = {};
    movedCompletedRef.current = {};
    seenCompletedRef.current = null;
  }, [date]);

  // A finished job stays on its active tab for ~10s, then relocates to Completed.
  // Jobs already completed on load go straight to Completed (no hold).
  useEffect(() => {
    if (isLoading) return; // wait for the first real snapshot so reloads hydrate Completed immediately
    const doneIds = cleans.filter((c) => c.session?.status === "completed").map((c) => c.id);
    const doneSet = new Set(doneIds);
    let changed = false;
    if (seenCompletedRef.current === null) {
      doneIds.forEach((id) => { if (!movedCompletedRef.current[id]) { movedCompletedRef.current[id] = true; changed = true; } });
    } else {
      doneIds.forEach((id) => {
        if (!movedCompletedRef.current[id] && !completedTimers.current[id]) {
          completedTimers.current[id] = setTimeout(() => {
            movedCompletedRef.current[id] = true;
            delete completedTimers.current[id];
            forceTick((t) => t + 1);
          }, 10000);
        }
      });
    }
    // a reopened room drops back to the active list
    Object.keys(movedCompletedRef.current).forEach((id) => {
      if (!doneSet.has(id)) { delete movedCompletedRef.current[id]; changed = true; }
    });
    Object.keys(completedTimers.current).forEach((id) => {
      if (!doneSet.has(id)) { clearTimeout(completedTimers.current[id]); delete completedTimers.current[id]; }
    });
    seenCompletedRef.current = doneSet;
    if (changed) forceTick((t) => t + 1);
  }, [cleans, isLoading]);

  const [editTimeClean, setEditTimeClean] = useState(null);
  const [viewTimesClean, setViewTimesClean] = useState(null);
  const [guestEditClean, setGuestEditClean] = useState(null);
  const [stayFor, setStayFor] = useState(null); // clean/row whose Long stay is being edited
  const [stayConflicts, setStayConflicts] = useState(null); // CSV upload paused on long-stay questions
  const [showEndedStays, setShowEndedStays] = useState(false);
  const { data: allStays = [] } = useQuery({
    queryKey: ["stays", date, showEndedStays],
    queryFn: async () => (await api.get(`/stays?date=${date}${showEndedStays ? "&include_ended=true" : ""}`)).data,
  });
  const stays = allStays.filter((s) => !s.ended);
  const endedStays = allStays.filter((s) => s.ended);
  const [staysOpen, setStaysOpen] = useState(false);
  const [staySel, setStaySel] = useState(null); // null = normal; Set of "stayId|taskId" when bulk-moving
  const [stayBulkDate, setStayBulkDate] = useState("");
  const stayBulk = useMutation({
    mutationFn: (body) => api.post("/stays/reschedule-bulk", body),
    onSuccess: ({ data }) => {
      toast.success(t("Moved {{count}} long stay task(s)", { count: data.moved }));
      if (data.skipped?.length) toast.warning(t("Not moved (after check-out): {{list}}", { list: data.skipped.join(", ") }));
      qc.invalidateQueries({ queryKey: ["stays"] });
      qc.invalidateQueries({ queryKey: ["cleans"] });
      setStaySel(null);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const bulkItems = () => [...(staySel || [])].map((k) => { const [stay_id, task_id] = k.split("|"); return { stay_id, task_id }; });
  const deleteStay = useMutation({
    mutationFn: (id) => api.delete(`/stays/${id}`),
    onSuccess: () => { toast.success(t("Long stay removed")); qc.invalidateQueries({ queryKey: ["stays"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const toggleStaySel = (k) => setStaySel((cur) => { const n = new Set(cur || []); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const stayDueToday = stays.flatMap((s) => (s.check_in <= date && s.check_out >= date ? (s.tasks || []).filter((tk) => tk.next_due <= date).map((tk) => ({ ...tk, room: s.room_number })) : []));
  const stayByRoom = Object.fromEntries(stays.filter((s) => s.check_in <= date && s.check_out >= date).map((s) => [s.room_number, s]));
  const setGuest = useMutation({
    mutationFn: ({ cleanId, ...body }) => api.post(`/cleans/${cleanId}/guest`, body),
    onSuccess: () => {
      toast.success(t("Guest details updated"));
      setGuestEditClean(null);
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const editTime = useMutation({
    mutationFn: ({ cleanId, ...body }) => api.post(`/cleans/${cleanId}/edit-time`, body),
    onSuccess: () => {
      toast.success(t("Times updated"));
      setEditTimeClean(null);
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const moveTime = useMutation({
    mutationFn: ({ cleanId, ...body }) => api.post(`/cleans/${cleanId}/move-time`, body),
    onSuccess: (res) => {
      toast.success(t("Time moved from Room {{from}} to Room {{to}}", { from: res.data.from, to: res.data.to }));
      setEditTimeClean(null);
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const [deferGeneral, setDeferGeneral] = useState(null);
  const deferMut = useMutation({
    mutationFn: ({ taskDefId, to_date }) =>
      api.post(`/task-defs/${taskDefId}/defer`, { occurrence: date, to_date }),
    onSuccess: (res) => {
      toast.success(t("Deferred to {{date}}", { date: res.data.to_date }));
      setDeferGeneral(null);
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const skipGeneralMut = useMutation({
    mutationFn: (taskDefId) => api.post(`/task-defs/${taskDefId}/skip`, { occurrence: date }),
    onSuccess: (res) => {
      toast.success(res.data.next_due_date ? t("Removed for today — back on {{date}}", { date: res.data.next_due_date }) : t("Removed for today"));
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const uploadExisting = cleans.filter((c) => c.id && c.session?.status !== "idle" && !c.is_extra && !c.is_general && !c.is_strip_task);
  const uploadActive = uploadExisting.filter((c) => c.session?.status !== "pending");
  const uploadIsPast = date < todayNZ();
  const uploadLocked = uploadIsPast || (uploadExisting.length > 0 && uploadActive.length === uploadExisting.length);

  const doUpload = async (stayDecisions) => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error(t("Choose a CSV file first"));
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("date", date);
    if (stayDecisions) fd.append("stay_decisions", JSON.stringify(stayDecisions));
    try {
      const { data } = await api.post("/csv/upload", fd);
      if (data.needs_stay_decisions) {
        setStayConflicts(data.conflicts);
        return;
      }
      setStayConflicts(null);
      toast.success(t("Imported {{count}} rooms for {{date}}", { count: data.created, date }));
      if (data.active_not_in_csv?.length) {
        toast.warning(
          t("Kept {{count}} room(s) already started or finished that are NOT in this CSV: {{rooms}}", { count: data.active_not_in_csv.length, rooms: data.active_not_in_csv.join(", ") }),
          { duration: 12000 },
        );
      }
      setUploadOpen(false);
      setReplaceAck(false);
      qc.invalidateQueries({ queryKey: ["cleans", date] });
      qc.invalidateQueries({ queryKey: ["rooms"] });
      qc.invalidateQueries({ queryKey: ["stays"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const toClean = cleans.filter((c) => c.needs_clean && !c.is_extra && !c.is_general);
  const counts = {
    total: cleans.filter((c) => !c.is_extra && !c.is_general && !c.is_reclean).length,
    toClean: toClean.length,
    departing: toClean.filter((c) => c.status === "departing" || c.status === "due").length,
    in_house: toClean.filter((c) => c.status === "in_house").length,
    priority: toClean.filter((c) => c.is_priority).length,
    completed: toClean.filter((c) => c.session?.status === "completed").length,
    unassigned: toClean.filter((c) => (c.assigned_cleaner_ids || []).length === 0).length,
    undelivered: cleans.filter((c) => c.status_undelivered).length,
    stripTotal: toClean.filter((c) => c.clean_type === "full" && c.needs_clean !== false).length,
    stripped: toClean.filter((c) => c.clean_type === "full" && c.needs_clean !== false && c.strip?.done).length,
  };
  const undeliveredRooms = cleans.filter((c) => c.status_undelivered);

  const isAssigned = (c) => (c.assigned_cleaner_ids || []).length > 0;
  // A non-room task (scheduled/extra) counts as "assigned" if it has assigned cleaners
  // or has been claimed by someone.
  const taskHasOwner = (c) => (c.assigned_cleaner_ids || []).length > 0 || !!c.claimed_by;
  const isMoved = (c) => !!movedCompletedRef.current[c.id];
  const roomsOnly = cleans.filter((c) => !c.is_extra && !c.is_general);
  const generalsList = cleans.filter((c) => c.is_general && !isMoved(c));
  const extrasList = cleans.filter((c) => c.is_extra && !isMoved(c));
  const activeRooms = roomsOnly.filter((c) => !isMoved(c));
  const assignedCount = activeRooms.filter(isAssigned).length;
  const unassignedCount = activeRooms.filter((c) => !isAssigned(c)).length;
  const filteredRooms = activeRooms.filter((c) => {
    if (filter === "assigned") return isAssigned(c);
    if (filter === "unassigned") return !isAssigned(c);
    return true;
  });
  // Scheduled tasks / extras respect the assigned/not-assigned filter too, so an
  // assigned task no longer leaks into the "Not assigned" list.
  const passesTaskFilter = (c) => {
    if (filter === "assigned") return taskHasOwner(c);
    if (filter === "unassigned") return !taskHasOwner(c);
    return true;
  };
  // Sort helpers. Room number always ascending (lowest→highest). "Task type" groups
  // service cleans first, then other cleans, then rooms with no clean scheduled.
  const roomCmp = (a, b) => {
    const na = parseInt(a.room_number, 10);
    const nb = parseInt(b.room_number, 10);
    if (!isNaN(na) && !isNaN(nb) && na !== nb) return na - nb;
    return String(a.room_number ?? "").localeCompare(String(b.room_number ?? ""), undefined, { numeric: true });
  };
  const typeRank = (c) => (c.clean_type === "service" ? 0 : (c.clean_type == null || c.needs_clean === false) ? 2 : 1);
  const sortedRooms = [...filteredRooms].sort((a, b) =>
    sortBy === "type" ? (typeRank(a) - typeRank(b)) || roomCmp(a, b) : roomCmp(a, b)
  );
  const filteredCleans = [...sortedRooms, ...generalsList.filter(passesTaskFilter), ...extrasList.filter(passesTaskFilter)];

  // Completed tab — every finished job that has settled (after its 10s hold), motel-wide.
  const completedList = cleans.filter((c) => isMoved(c));
  const completedCount = cleans.filter((c) => c.session?.status === "completed").length;

  // "To Be Stripped" tab — full (departure) cleans not yet stripped, motel-wide.
  // Recently-stripped rooms are held for 10s so the stripper can undo before they drop off.
  const needsStripping = (c) => c.needs_clean && c.clean_type === "full" && !(c.strip && c.strip.done);
  const stripRooms = roomsOnly.filter((c) => needsStripping(c) || heldStrip[c.id]);
  const stripCount = roomsOnly.filter(needsStripping).length;

  const markStrippedInList = (clean) => {
    setHeldStrip((m) => ({ ...m, [clean.id]: true }));
    setStrip.mutate({ cleanId: clean.id, done: true });
    if (stripTimers.current[clean.id]) clearTimeout(stripTimers.current[clean.id]);
    stripTimers.current[clean.id] = setTimeout(() => {
      setHeldStrip((m) => {
        const n = { ...m };
        delete n[clean.id];
        return n;
      });
      delete stripTimers.current[clean.id];
      qc.invalidateQueries({ queryKey: ["cleans", date] });
    }, 10000);
  };

  const undoStrippedInList = (clean) => {
    if (stripTimers.current[clean.id]) {
      clearTimeout(stripTimers.current[clean.id]);
      delete stripTimers.current[clean.id];
    }
    setHeldStrip((m) => {
      const n = { ...m };
      delete n[clean.id];
      return n;
    });
    setStrip.mutate({ cleanId: clean.id, done: false });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold">{t("Daily Cleaning List")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("All rooms in room order. Upload the housekeeping CSV to flag today's cleans and auto-assign them.")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/manager/rooms">
            <Button variant="outline" data-testid="manage-rooms-link" className="k-press gap-2">
              <DoorClosed className="h-4 w-4" /> Manage Rooms
            </Button>
          </Link>
          <DatePicker value={date} onChange={setDate} testid="dashboard-date-picker" />
          <Button
            variant="outline"
            size="icon"
            onClick={() => qc.invalidateQueries({ queryKey: ["cleans", date] })}
            data-testid="refresh-cleans-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => setUploadOpen(true)} data-testid="open-upload-btn" className="k-press gap-2">
            <Upload className="h-4 w-4" />
            {t("Upload CSV")}
          </Button>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="daily-stats">
        <StatTile label={t("Total Rooms")} value={counts.total} />
        <StatTile label={t("Departing")} value={counts.departing} tone="departing" />
        <StatTile label={t("In House")} value={counts.in_house} tone="in_house" />
        <StatTile label={t("Due Priority")} value={counts.priority} tone="priority" />
        <StatTile label={t("Completed")} value={`${counts.completed}/${counts.toClean}`} />
        {counts.stripTotal > 0 && (
          <span data-testid="stripped-count-tile" title={t("Departure rooms stripped so far today")}>
            <StatTile label={t("Stripped")} value={`${counts.stripped}/${counts.stripTotal}`} tone={counts.stripped === counts.stripTotal ? "stripped" : undefined} />
          </span>
        )}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-md border border-border bg-card p-1" data-testid="assignment-filter">
          {[
            { k: "all", label: `${t("All")} (${activeRooms.length})` },
            { k: "assigned", label: `${t("Assigned")} (${assignedCount})` },
            { k: "unassigned", label: `${t("Not assigned")} (${unassignedCount})` },
            { k: "strip", label: `${t("To Be Stripped")} (${stripCount})` },
            { k: "completed", label: `${t("Completed")} (${completedCount})` },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`filter-${f.k}`}
              className={`k-press rounded px-3 py-1.5 text-sm font-medium ${
                filter === f.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-muted-foreground" data-testid="compact-toggle-label">
          <Switch checked={compact} onCheckedChange={toggleCompact} data-testid="compact-toggle" />
          {t("Compact view")}
        </label>
        <SortControl sortBy={sortBy} onChange={changeSort} />
      </div>

      <div className="rounded-md border border-indigo-200 bg-indigo-50/40 px-4 py-2.5" data-testid="long-stay-panel">
          <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setStaysOpen((o) => !o)}
            aria-expanded={staysOpen}
            data-testid="long-stay-toggle"
            className="k-press flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-semibold text-indigo-900"
          >
            <BedDouble className="h-4 w-4 shrink-0" />
            <span>{t("Long stays ({{count}})", { count: stays.length })}</span>
            {stays.length > 0 && (
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${stayDueToday.length ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid="long-stay-summary">
              {stayDueToday.length
                ? t("{{count}} long stay task(s) required today", { count: stayDueToday.length })
                : t("No long stay tasks today")}
            </span>
            )}
            {!staysOpen && stayDueToday.length > 0 && (
              <span className="hidden truncate text-xs font-normal text-muted-foreground md:inline">
                {stayDueToday.map((tk) => `${tk.room} · ${tk.name}`).join(", ")}
              </span>
            )}
            <ChevronDown className={`ml-auto h-4 w-4 shrink-0 transition-transform ${staysOpen ? "rotate-180" : ""}`} />
          </button>
          <Button variant="outline" size="sm" className="h-7 shrink-0 gap-1 text-xs" onClick={() => setStayFor({ room_number: "", guests: [], stay: null })} data-testid="long-stay-add-btn">
            <Plus className="h-3.5 w-3.5" /> {t("Add long stay")}
          </Button>
          </div>
          {staysOpen && stays.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="long-stay-bulk-bar">
            {staySel === null ? (
              <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => setStaySel(new Set())} data-testid="long-stay-bulk-start">
                <CalendarClock className="h-3.5 w-3.5" /> {t("Move several tasks")}
              </Button>
            ) : (
              <>
                <span className="font-semibold text-indigo-900" data-testid="long-stay-bulk-count">{t("{{count}} selected", { count: staySel.size })}</span>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => {
                  const weekEnd = new Date(`${date}T00:00:00`); weekEnd.setDate(weekEnd.getDate() + 6);
                  const we = `${weekEnd.getFullYear()}-${String(weekEnd.getMonth() + 1).padStart(2, "0")}-${String(weekEnd.getDate()).padStart(2, "0")}`;
                  setStaySel(new Set(stays.flatMap((s) => (s.tasks || []).filter((tk) => tk.next_due <= we && tk.next_due <= s.check_out).map((tk) => `${s.id}|${tk.id}`))));
                }} data-testid="long-stay-bulk-all">{t("Select all shown")}</Button>
                <span className="mx-1 h-4 w-px bg-border" />
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!staySel.size || stayBulk.isPending} onClick={() => stayBulk.mutate({ items: bulkItems(), shift_days: -1 })} data-testid="long-stay-bulk-earlier">◀ {t("1 day earlier")}</Button>
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!staySel.size || stayBulk.isPending} onClick={() => stayBulk.mutate({ items: bulkItems(), shift_days: 1 })} data-testid="long-stay-bulk-later">{t("1 day later")} ▶</Button>
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!staySel.size || stayBulk.isPending} onClick={() => stayBulk.mutate({ items: bulkItems(), date: todayNZ() })} data-testid="long-stay-bulk-today">{t("Today")}</Button>
                <Input type="date" min={todayNZ()} value={stayBulkDate} onChange={(e) => setStayBulkDate(e.target.value)} className="h-7 w-[140px] text-xs" data-testid="long-stay-bulk-date" />
                <Button size="sm" className="h-7 gap-1 text-xs" disabled={!staySel.size || !stayBulkDate || stayBulk.isPending} onClick={() => stayBulk.mutate({ items: bulkItems(), date: stayBulkDate })} data-testid="long-stay-bulk-move">
                  {stayBulk.isPending && <Loader2 className="h-3 w-3 animate-spin" />} {t("Move to date")}
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setStaySel(null)} data-testid="long-stay-bulk-cancel">{t("Cancel")}</Button>
              </>
            )}
          </div>
          )}
          {staysOpen && (
          <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
            {stays.map((s) => {
              const weekEnd = new Date(`${date}T00:00:00`); weekEnd.setDate(weekEnd.getDate() + 6);
              const we = `${weekEnd.getFullYear()}-${String(weekEnd.getMonth() + 1).padStart(2, "0")}-${String(weekEnd.getDate()).padStart(2, "0")}`;
              const due = (s.tasks || []).filter((tk) => tk.next_due <= we && tk.next_due <= s.check_out).sort((a, b) => a.next_due.localeCompare(b.next_due));
              const upcoming = s.check_in > date;
              return (
                <div
                  key={s.id}
                  data-testid={`long-stay-card-${s.room_number}`}
                  className="rounded-md border border-border bg-card px-3 py-2 text-left text-xs"
                >
                  <button
                    type="button"
                    onClick={() => setStayFor({ room_number: s.room_number, guests: [{ name: s.guest_name }], stay: s })}
                    data-testid={`long-stay-card-open-${s.room_number}`}
                    className="k-press flex w-full items-center justify-between gap-2 rounded hover:bg-secondary/50"
                  >
                    <span className="font-display text-base font-extrabold">{s.room_number}</span>
                    <span className="truncate text-muted-foreground">{s.guest_name || t("Guest")}</span>
                    {upcoming && <span className="shrink-0 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-800" data-testid={`long-stay-upcoming-${s.room_number}`}>{t("Upcoming")}</span>}
                    <span className="shrink-0 text-muted-foreground">{upcoming ? t("from {{date}}", { date: fmtDMY(s.check_in) }) : t("until {{date}}", { date: fmtDMY(s.check_out) })}</span>
                  </button>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    {staySel !== null && due.length > 0 && (
                      <button type="button" className="rounded border border-dashed px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-secondary" data-testid={`long-stay-select-room-${s.room_number}`}
                        onClick={() => setStaySel((cur) => { const n = new Set(cur || []); const keys = due.map((tk) => `${s.id}|${tk.id}`); const all = keys.every((k) => n.has(k)); keys.forEach((k) => (all ? n.delete(k) : n.add(k))); return n; })}>
                        {t("All in room")}
                      </button>
                    )}
                    {due.length === 0 ? (
                      <span className="italic text-muted-foreground">{t("Nothing due this week")}</span>
                    ) : due.map((tk) => (
                      <StayTaskChip key={tk.id} stay={s} task={tk} date={date}
                        selectable={staySel !== null} selected={!!staySel?.has(`${s.id}|${tk.id}`)} onToggleSelect={() => toggleStaySel(`${s.id}|${tk.id}`)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          )}
          {staysOpen && (
          <div className="mt-2 border-t border-indigo-200 pt-2" data-testid="long-stay-ended-section">
            <button type="button" onClick={() => setShowEndedStays((v) => !v)} className="text-xs font-medium text-indigo-800 underline-offset-2 hover:underline" data-testid="long-stay-show-ended">
              {showEndedStays ? t("Hide ended / closed stays") : t("Show ended / closed stays")}
            </button>
            {showEndedStays && (
              endedStays.length === 0 ? (
                <p className="mt-1 text-xs italic text-muted-foreground" data-testid="long-stay-ended-empty">{t("No stays ended in the last 60 days.")}</p>
              ) : (
              <div className="mt-1.5 grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                {endedStays.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 rounded-md border border-dashed bg-card/60 px-3 py-2 text-xs text-muted-foreground" data-testid={`long-stay-ended-card-${s.room_number}`}>
                    <span className="font-display text-base font-extrabold text-foreground">{s.room_number}</span>
                    <span className="min-w-0 flex-1 truncate">{s.guest_name || t("Guest")} · {s.closed_by_departure ? t("ended by departure {{date}}", { date: fmtDMY(s.closed_by_departure) }) : t("ended {{date}}", { date: fmtDMY(s.check_out) })}</span>
                    <Button variant="outline" size="sm" className="h-6 px-2 text-[11px]" onClick={() => setStayFor({ room_number: s.room_number, guests: [{ name: s.guest_name }], stay: s })} data-testid={`long-stay-reopen-${s.room_number}`}>{t("Reopen")}</Button>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-rose-700" disabled={deleteStay.isPending} onClick={() => deleteStay.mutate(s.id)} data-testid={`long-stay-delete-${s.room_number}`}>{t("Delete")}</Button>
                  </div>
                ))}
              </div>
              )
            )}
          </div>
          )}
        </div>

      {holidayToday && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900" data-testid="holiday-banner">
          <Sparkles className="h-4 w-4" />
          <span className="font-semibold">{t("Public holiday")}: {holidayToday}.</span>
          <span>
            {rosterDay?.public_holiday_rule === "rostered"
              ? t("Normal roster applies — anyone who works is marked \"Public holiday worked\" on their timesheet.")
              : t("Everyone is off by default. Add a cleaner below if they've agreed to work — their timesheet will show \"Public holiday worked\".")}
          </span>
          {rosterDay?.public_holiday_rule !== "rostered" && (
            <Button size="sm" variant="outline" className="ml-auto h-7 gap-1 border-amber-400 bg-white text-xs" onClick={() => setAskVolunteers(true)} data-testid="ask-volunteers-btn">
              <Megaphone className="h-3.5 w-3.5" /> {t("Ask who can work")}
            </Button>
          )}
          <HolidayRequestStatus date={date} />
        </div>
      )}
      {askVolunteers && (
        <HolidayVolunteerDialog
          open={askVolunteers}
          onClose={() => setAskVolunteers(false)}
          date={date}
          holiday={holidayToday}
          cleaners={rosteredOffToday.filter((c) => c.kind === "holiday").map((c) => ({ id: c.id, name: c.name }))}
        />
      )}
      <UpcomingHolidays date={date} />
      {(rosteredOffToday.length > 0 || addedToday.length > 0 || absentToday.length > 0 || leaveToday.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50/60 px-4 py-2.5" data-testid="roster-off-banner">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-indigo-800">
            <MoonStar className="h-4 w-4" /> {t("Rostered off today")}:
          </span>
          {rosteredOffToday.length === 0 && leaveToday.length === 0 && absentToday.length === 0 && addedToday.length > 0 && (
            <span className="text-sm text-muted-foreground">{t("none")}</span>
          )}
          {leaveToday.map((c) => (
            <span key={c.id} data-testid={`leave-today-${c.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-violet-300 bg-violet-50 px-3 py-1 text-sm font-medium text-violet-800" title={t("Set under Cleaners → Leave")}>
              <CalendarRange className="h-3.5 w-3.5" /> {c.name} · {t(c.why)}
            </span>
          ))}
          {rosteredOffToday.map((c) => (
            <span
              key={c.id}
              data-testid={`off-today-${c.id}`}
              className="inline-flex items-center gap-1 rounded-full border border-indigo-300 bg-white py-0.5 pl-1 pr-1 text-sm font-medium text-indigo-700"
            >
              <button
                type="button"
                disabled={dayRosterMut.isPending}
                onClick={() => dayRosterMut.mutate({ cleanerId: c.id, on: true })}
                data-testid={`add-to-day-${c.id}`}
                className="k-press inline-flex items-center gap-1 rounded-full px-2 hover:bg-indigo-100"
                title={holidayToday ? t("{{name}} has agreed to work this public holiday — add them", { name: c.name }) : t("Add {{name}} to today's shift", { name: c.name })}
              >
                <Plus className="h-3.5 w-3.5" /> {c.name}
              </button>
              <select
                value=""
                disabled={dayAbsenceMut.isPending}
                onChange={(e) => e.target.value && dayAbsenceMut.mutate({ cleanerId: c.id, label: e.target.value })}
                data-testid={`off-reason-select-${c.id}`}
                className="h-6 max-w-[230px] rounded-full border-0 bg-indigo-100 px-1.5 text-xs text-indigo-900 outline-none hover:bg-indigo-200"
                title={t("Timesheet shows: {{label}} — change the reason", { label: t(dayLabels[c.id] || c.why || "Rostered day off") })}
              >
                <option value="">{t(dayLabels[c.id] || c.why || "Rostered day off")}</option>
                {ABSENCE_LABELS.filter((l) => l !== (dayLabels[c.id] || c.why || "Rostered day off")).map((l) => <option key={l} value={l}>{t(l)}</option>)}
              </select>
            </span>
          ))}
          {addedToday.map((c) => (
            <span
              key={c.id}
              data-testid={`added-today-${c.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800"
            >
              {c.name} · {t("added")}
              <button
                type="button"
                disabled={dayRosterMut.isPending}
                onClick={() => dayRosterMut.mutate({ cleanerId: c.id, on: false })}
                data-testid={`remove-from-day-${c.id}`}
                className="k-press rounded-full hover:bg-emerald-200"
                title={t("Remove from today")}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          {absentToday.map((c) => (
            <span
              key={c.id}
              data-testid={`absent-today-${c.id}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-rose-300 bg-rose-50 px-3 py-1 text-sm font-medium text-rose-800"
            >
              {c.name} ·
              <select
                value={c.day_absences[date]}
                disabled={dayAbsenceMut.isPending}
                onChange={(e) => e.target.value && dayAbsenceMut.mutate({ cleanerId: c.id, label: e.target.value })}
                data-testid={`absent-reason-select-${c.id}`}
                className="h-6 max-w-[160px] rounded-full border-0 bg-rose-100 px-1.5 text-xs font-medium text-rose-900 outline-none hover:bg-rose-200"
                title={t("Change the reason shown on the timesheet")}
              >
                {[c.day_absences[date], ...ABSENCE_LABELS.filter((l) => l !== c.day_absences[date])].map((l) => <option key={l} value={l}>{t(l)}</option>)}
              </select>
              <button
                type="button"
                disabled={dayAbsenceMut.isPending}
                onClick={() => dayAbsenceMut.mutate({ cleanerId: c.id, label: "" })}
                data-testid={`clear-absence-${c.id}`}
                className="k-press rounded-full hover:bg-rose-200"
                title={t("Put back on today's shift")}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <RosterUnseenNudge />
      {onToday.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50/60 px-4 py-2.5" data-testid="roster-on-banner">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800">
            <Users className="h-4 w-4" /> {t("Rostered on today")}:
          </span>
          {onToday.map((c) => (
            <label key={c.id} className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white py-0.5 pl-3 pr-1 text-sm font-medium text-emerald-800" data-testid={`on-today-${c.id}`}>
              {c.name}
              {c.why && c.why !== "Rostered on" && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700" data-testid={`on-why-${c.id}`}>· {t(c.why)}</span>}
              <select
                value=""
                disabled={dayAbsenceMut.isPending}
                onChange={(e) => e.target.value && dayAbsenceMut.mutate({ cleanerId: c.id, label: e.target.value })}
                data-testid={`absence-select-${c.id}`}
                className="h-6 max-w-[120px] rounded-full border-0 bg-emerald-100 px-1.5 text-xs text-emerald-900 outline-none hover:bg-emerald-200"
                title={t("Mark {{name}} absent today", { name: c.name })}
              >
                <option value="">{t("ON")}</option>
                <option value="" disabled>{t("— Mark absent —")}</option>
                {ABSENCE_LABELS.map((l) => <option key={l} value={l}>{t(l)}</option>)}
              </select>
            </label>
          ))}
        </div>
      )}

      {counts.undelivered > 0 && (
        <div
          className="flex flex-col gap-0.5 rounded-md border border-orange-300 bg-orange-50 px-4 py-2.5 text-sm font-medium text-orange-800"
          data-testid="undelivered-summary"
        >
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {counts.undelivered} status change{counts.undelivered > 1 ? "s" : ""} not yet synced to a cleaner — inform them manually if they're offline.
          </span>
          {undeliveredRooms.map((c) => (
            <span key={c.id} className="pl-6 text-xs">
              Room {c.room_number} → {c.status === "in_house" ? "In-house" : "Departing"}
              {(c.undelivered_to || []).length > 0 && `  ·  awaiting: ${c.undelivered_to.join(", ")}`}
            </span>
          ))}
        </div>
      )}

      {counts.unassigned > 0 && counts.toClean > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          {counts.unassigned} room{counts.unassigned > 1 ? "s" : ""} to clean today are not yet assigned to a cleaner.
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : cleans.length === 0 ? (
        <EmptyState onUpload={() => setUploadOpen(true)} />
      ) : filter === "strip" ? (
        <div className="space-y-2" data-testid="strip-list">
          {stripRooms.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-16 text-center">
              <CheckCircle2 className="h-9 w-9 text-emerald-500" />
              <p className="text-sm text-muted-foreground">{t("All departure rooms are stripped.")}</p>
            </div>
          ) : (
            stripRooms.map((c) => {
              const held = !!heldStrip[c.id];
              const g = (c.guests || [])[0];
              const num = (s) => { const m = String(s ?? "").match(/\d+/); return m ? m[0] : ""; };
              const gA = g ? num(g.adults) : "";
              const gC = g ? num(g.children) : "";
              return (
                <div
                  key={c.id}
                  data-testid={`strip-row-${c.room_number}`}
                  className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${held ? "border-emerald-200 bg-emerald-50" : "border-border bg-card"}`}
                >
                  <div className="w-14 shrink-0 text-2xl font-extrabold text-foreground">{c.room_number}</div>
                  <div className="min-w-0 flex-1 text-xs text-muted-foreground">
                    {c.room_type || "Room"}
                    {c.is_priority && <span className="ml-1 font-semibold text-red-600">· Priority</span>}
                    {g && (
                      <span className="ml-2 inline-flex items-center gap-1" data-testid={`strip-guest-${c.room_number}`}>
                        <User className="h-3 w-3 shrink-0" />
                        <span className="font-medium text-foreground">{g.name || "—"}</span>
                        {(gA || gC) && <span>· {gA || 0}A{gC && Number(gC) > 0 ? ` ${gC}C` : ""}</span>}
                      </span>
                    )}
                    {c.checkout_time && defaultCheckout && c.checkout_time > defaultCheckout && (
                      <span
                        data-testid={`late-checkout-badge-${c.room_number}`}
                        className="ml-2 inline-flex items-center gap-1 rounded-md border border-orange-300 bg-orange-50 px-1.5 py-0.5 text-[11px] font-semibold text-orange-700"
                        title="Late checkout — don't strip until this time"
                      >
                        <Clock className="h-3 w-3" /> Late {c.checkout_time}
                      </span>
                    )}
                    <div className="mt-1">
                      <CheckoutTimeControl clean={c} defaultCheckout={defaultCheckout} onSetCheckout={(time) => setCheckout.mutate({ cleanId: c.id, time })} />
                    </div>
                  </div>
                  {held ? (
                    <div className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-sm font-semibold text-emerald-700">
                        <CheckCircle2 className="h-4 w-4" /> Stripped
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`strip-undo-${c.room_number}`}
                        onClick={() => undoStrippedInList(c)}
                        className="k-press h-9 gap-1 text-xs font-semibold"
                      >
                        <RotateCcw className="h-3.5 w-3.5" /> Undo
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {!c.key_returned ? (
                        <Button
                          data-testid={`strip-key-return-${c.room_number}`}
                          onClick={() => setKeyReturn.mutate({ cleanId: c.id, returned: true })}
                          className="k-press h-10 shrink-0 gap-2 bg-sky-600 font-bold hover:bg-sky-700"
                        >
                          <KeyRound className="h-4 w-4" /> Key Returned
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`strip-key-undo-${c.room_number}`}
                            onClick={() => setKeyReturn.mutate({ cleanId: c.id, returned: false })}
                            className="k-press h-9 gap-1 text-xs font-semibold text-muted-foreground"
                          >
                            <RotateCcw className="h-3.5 w-3.5" /> Undo key
                          </Button>
                          <Button
                            data-testid={`strip-mark-${c.room_number}`}
                            onClick={() => markStrippedInList(c)}
                            className="k-press h-10 shrink-0 gap-2 bg-amber-600 font-bold hover:bg-amber-700"
                          >
                            <Sparkles className="h-4 w-4" /> Mark stripped
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      ) : filter === "completed" ? (
        <div className={compact ? "space-y-1" : "space-y-2.5"} data-testid="completed-list">
          {completedList.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-16 text-center">
              <CheckCircle2 className="h-9 w-9 text-emerald-500" />
              <p className="text-sm text-muted-foreground">{t("No completed jobs yet today.")}</p>
            </div>
          ) : (
            <>
              {(() => {
                const roomsDone = completedList.filter((c) => !c.is_general && !c.is_extra);
                const durs = roomsDone.map(cleanNetMin).filter((v) => v != null);
                const avg = durs.length ? Math.round(durs.reduce((a, b) => a + b, 0) / durs.length) : null;
                return (
                  <div className="mb-2 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800" data-testid="completed-summary">
                    <CheckCircle2 className="h-4 w-4" />
                    {roomsDone.length} room{roomsDone.length === 1 ? "" : "s"} done{avg != null ? ` · avg ${avg} min each` : ""}
                  </div>
                );
              })()}
              {completedList.map((c) => (
                <CleanRow
                  key={c.id || `room-${c.room_number}`}
                  clean={c}
                  cleaners={cleaners}
                  defaultCheckout={defaultCheckout}
                  compact={compact}
                  onAssign={assign.mutate}
                  onAddSpecial={() => setSpecialClean(c)}
                  onRemoveSpecial={(itemId) => removeSpecial.mutate({ cleanId: c.id, itemId })}
                  onDefer={() => setDeferGeneral(c)}
                  onSkip={() => { if (window.confirm(`Remove "${c.name || c.room_number}" from today's list? It comes back on its next scheduled day.`)) skipGeneralMut.mutate(c.task_def_id); }}
                  onChangeStatus={(status) => changeStatus.mutate({ cleanId: c.id, status })}
                  onUndoDnd={() => undoDndMut.mutate(c.id)}
                  onSetState={(state) => setState.mutate({ roomNumber: c.room_number, state })}
                  onEarlyCheckout={() => setEarlyCheckout(c)}
                  onRemoveEarlyCheckout={() => removeEarlyCheckoutMut.mutate(c.id)}
                  onStrip={(done) => setStrip.mutate({ cleanId: c.id, done })}
                  onKeyReturn={(returned) => setKeyReturn.mutate({ cleanId: c.id, returned })}
                  onSetCheckout={(time) => setCheckout.mutate({ cleanId: c.id, time })}
                  onEditTime={() => setEditTimeClean(c)}
                  onViewTimes={() => setViewTimesClean(c)}
                  onFixStrip={() => setFixStrip(c)}
                  onEditGuest={() => setGuestEditClean(c)}
                  stay={stayByRoom[c.room_number]}
                  onStay={() => setStayFor(c)}
                />
              ))}
            </>
          )}
        </div>
      ) : (
        <div className={compact ? "space-y-1" : "space-y-2.5"} data-testid="cleans-list">
          {filteredCleans.map((c) => (
            <CleanRow
              key={c.id || `room-${c.room_number}`}
              clean={c}
              cleaners={cleaners}
              defaultCheckout={defaultCheckout}
              compact={compact}
              onAssign={assign.mutate}
              onAddSpecial={() => setSpecialClean(c)}
              onRemoveSpecial={(itemId) => removeSpecial.mutate({ cleanId: c.id, itemId })}
              onDefer={() => setDeferGeneral(c)}
              onSkip={() => { if (window.confirm(`Remove "${c.name || c.room_number}" from today's list? It comes back on its next scheduled day.`)) skipGeneralMut.mutate(c.task_def_id); }}
              onChangeStatus={(status) => changeStatus.mutate({ cleanId: c.id, status })}
              onUndoDnd={() => undoDndMut.mutate(c.id)}
              onSetState={(state) => setState.mutate({ roomNumber: c.room_number, state })}
              onEarlyCheckout={() => setEarlyCheckout(c)}
              onRemoveEarlyCheckout={() => removeEarlyCheckoutMut.mutate(c.id)}
              onStrip={(done) => setStrip.mutate({ cleanId: c.id, done })}
              onKeyReturn={(returned) => setKeyReturn.mutate({ cleanId: c.id, returned })}
              onSetCheckout={(time) => setCheckout.mutate({ cleanId: c.id, time })}
              onEditTime={() => setEditTimeClean(c)}
              onViewTimes={() => setViewTimesClean(c)}
              onFixStrip={() => setFixStrip(c)}
              onEditGuest={() => setGuestEditClean(c)}
              stay={stayByRoom[c.room_number]}
              onStay={() => setStayFor(c)}
            />
          ))}
          {filteredCleans.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground">No rooms match this filter.</p>
          )}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { setUploadOpen(o); if (!o) setReplaceAck(false); }}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload Housekeeping CSV</DialogTitle>
            <DialogDescription>
              Import the daily short list for <strong data-testid="upload-target-date">{fmtDMY(date)}{date === todayNZ() ? " (today)" : ""}</strong>. Duplicate room rows are merged;
              a Departing + Due room becomes a full clean flagged as priority.
            </DialogDescription>
          </DialogHeader>
          {uploadLocked ? (
            <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800" data-testid="upload-locked">
              <Lock className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {uploadIsPast
                  ? t("{{date}} is a past day — its rooms are locked and can't be replaced. Pick today or a future date.", { date: fmtDMY(date) })
                  : t("Every room for this day is already started or finished, so the list is locked. To add a room, use 'Set status' on that room instead.")}
              </span>
            </div>
          ) : uploadExisting.length > 0 && (
            <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="upload-overwrite-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {t("{{date}} already has {{count}} room(s) loaded. Uploading will replace the {{pending}} not-yet-started room(s); {{active}} started/finished room(s) are kept as they are.", {
                    date: fmtDMY(date), count: uploadExisting.length, pending: uploadExisting.length - uploadActive.length, active: uploadActive.length,
                  })}
                </span>
              </div>
              <label className="flex cursor-pointer items-center gap-2 font-semibold">
                <Checkbox checked={replaceAck} onCheckedChange={(v) => setReplaceAck(!!v)} data-testid="upload-replace-ack" />
                {t("Yes, replace the current list for this day")}
              </label>
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            data-testid="csv-file-input"
            className="block w-full rounded-md border border-border bg-secondary/40 p-3 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-primary-foreground"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => doUpload()} disabled={uploading || uploadLocked || (uploadExisting.length > 0 && !replaceAck)} data-testid="confirm-upload-btn" className="gap-2">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SpecialDialog
        clean={specialClean}
        library={specialLib}
        onClose={() => setSpecialClean(null)}
        onAdd={(name, minutes) => {
          addSpecial.mutate({ cleanId: specialClean.id, name, minutes });
          setSpecialClean(null);
        }}
      />

      <Dialog open={!!earlyCheckout} onOpenChange={(o) => !o && setEarlyCheckout(null)}>
        <DialogContent data-testid="early-checkout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LogOut className="h-5 w-5 text-amber-600" /> {t("Early checkout?")}</DialogTitle>
            <DialogDescription>
              {t("Room {{room}} was already serviced today. Keep that service record and add a new full clean for the departure?", { room: earlyCheckout?.room_number })}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
            {t("The new clean is assigned to all cleaners so anyone can pick it up. It appears in room order with an Early Checkout badge.")}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              data-testid="early-checkout-just-status"
              onClick={() => { const c = earlyCheckout; setEarlyCheckout(null); setState.mutate({ roomNumber: c.room_number, state: "departing" }); }}
            >
              {t("Just change status")}
            </Button>
            <Button
              data-testid="early-checkout-confirm"
              disabled={earlyCheckoutMut.isPending}
              onClick={() => earlyCheckoutMut.mutate(earlyCheckout.id)}
              className="gap-2"
            >
              {earlyCheckoutMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("Add Early Checkout clean")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GeneralDeferDialog
        clean={deferGeneral}
        fromDate={date}
        pending={deferMut.isPending}
        onClose={() => setDeferGeneral(null)}
        onDefer={(to_date) => deferMut.mutate({ taskDefId: deferGeneral.task_def_id, to_date })}
      />

      <FixStripDialog
        clean={fixStrip}
        cleaners={cleaners}
        managers={managers}
        pending={fixStripMut.isPending}
        onClose={() => setFixStrip(null)}
        onSave={(byId) => fixStripMut.mutate({ cleanId: fixStrip.id, byId })}
      />

      <EditTimeDialog
        clean={editTimeClean}
        rooms={roomsOnly}
        cleaners={cleaners}
        pending={editTime.isPending || moveTime.isPending}
        onClose={() => setEditTimeClean(null)}
        onSave={(body) => editTime.mutate({ cleanId: editTimeClean.id, ...body })}
        onMove={(to_clean_id, move_strip) => moveTime.mutate({ cleanId: editTimeClean.id, cleaner_id: editTimeClean.claimed_by, to_clean_id, move_strip })}
      />

      <TimeDetailsDialog clean={viewTimesClean} onClose={() => setViewTimesClean(null)} />

      <StayDialog
        open={!!stayFor}
        onClose={() => setStayFor(null)}
        roomNumber={stayFor?.room_number}
        stay={stayFor ? (stayFor.stay !== undefined ? stayFor.stay : stayByRoom[stayFor.room_number]) : null}
        guestName={(stayFor?.guests || [])[0]?.name || ""}
        date={date}
      />

      <StayCsvDecisionsDialog
        conflicts={stayConflicts}
        date={date}
        pending={uploading}
        onClose={() => setStayConflicts(null)}
        onConfirm={(decisions) => doUpload(decisions)}
      />

      <GuestEditDialog
        clean={guestEditClean}
        onClose={() => setGuestEditClean(null)}
        onSave={(body) => setGuest.mutate({ cleanId: guestEditClean.id, ...body })}
        pending={setGuest.isPending}
      />
    </div>
  );
}

function GuestEditDialog({ clean, onClose, onSave, pending }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [adults, setAdults] = useState("");
  const [children, setChildren] = useState("");
  useEffect(() => {
    const g = (clean?.guests || [])[0] || {};
    const num = (s) => { const m = String(s ?? "").match(/\d+/); return m ? m[0] : ""; };
    setName(g.name || "");
    setAdults(num(g.adults));
    setChildren(num(g.children));
  }, [clean]);
  if (!clean) return null;
  return (
    <Dialog open={!!clean} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="guest-edit-dialog">
        <DialogHeader>
          <DialogTitle>{t("Guest details")} — {t("Room")} {clean.room_number}</DialogTitle>
          <DialogDescription>{t("Set the guest name and how many adults and children are staying.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="guest-name">{t("Guest name")}</Label>
            <Input id="guest-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="guest-name-input" placeholder={t("Guest name")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="guest-adults">{t("Adults")}</Label>
              <Input id="guest-adults" type="number" min="0" value={adults} onChange={(e) => setAdults(e.target.value)} data-testid="guest-adults-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="guest-children">{t("Children")}</Label>
              <Input id="guest-children" type="number" min="0" value={children} onChange={(e) => setChildren(e.target.value)} data-testid="guest-children-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="guest-cancel-btn">{t("Cancel")}</Button>
          <Button
            onClick={() => onSave({ name, adults, children })}
            disabled={pending}
            data-testid="guest-save-btn"
          >
            {t("Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FixStripDialog({ clean, cleaners, managers, onClose, onSave, pending }) {
  const { t } = useTranslation();
  const currentId = clean?.strip?.by_id || null;
  const people = [
    ...(managers || []).map((m) => ({ id: m.id, name: m.name, role: "manager" })),
    ...(cleaners || []).map((c) => ({ id: c.id, name: c.name, role: "cleaner" })),
  ];
  return (
    <Dialog open={!!clean} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fix-strip-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-emerald-600" /> {t("Who stripped this room?")}</DialogTitle>
          <DialogDescription>
            {t("Room {{room}} is currently logged as stripped by {{name}}. Pick the person who actually stripped it — their time credit updates to match.", { room: clean?.room_number, name: clean?.strip?.by_name || "—" })}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] space-y-1.5 overflow-y-auto">
          {people.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={pending}
              onClick={() => { if (p.id !== currentId) onSave(p.id); }}
              data-testid={`fix-strip-person-${p.id}`}
              className={`k-press flex w-full items-center justify-between rounded-md border px-3 py-2.5 text-left text-sm transition-colors ${p.id === currentId ? "border-emerald-300 bg-emerald-50 font-semibold text-emerald-700" : "border-border hover:bg-muted/50"}`}
            >
              <span>{p.name}</span>
              <span className="text-xs text-muted-foreground">
                {p.role === "manager" ? t("Manager") : t("Cleaner")}{p.id === currentId ? ` · ${t("current")}` : ""}
              </span>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="fix-strip-cancel">{t("Cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function GeneralDeferDialog({ clean, fromDate, onClose, onDefer, pending }) {
  const nextDay = new Date((fromDate || "") + "T00:00:00");
  nextDay.setDate(nextDay.getDate() + 1);
  const minDate = nextDay.toISOString().slice(0, 10);
  const [toDate, setToDate] = useState(minDate);
  if (!clean) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="general-defer-dialog">
        <DialogHeader>
          <DialogTitle>Defer "{clean.room_number}"</DialogTitle>
          <DialogDescription>
            Move this occurrence off {fromDate} to a later date. The task's recurrence stays the same.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>New date</Label>
          <Input
            type="date"
            min={minDate}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            data-testid="general-defer-date"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={pending || !toDate}
            onClick={() => onDefer(toDate)}
            data-testid="general-defer-confirm"
            className="gap-2"
          >
            <CalendarClock className="h-4 w-4" /> Defer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function toLocalInput(iso) {
  return isoToNZInput(iso);
}
function fromLocalInput(v) {
  return nzInputToISO(v);
}

function workerNetSeconds(s) {
  if (!s?.started_at) return 0;
  const end = s.ended_at ? new Date(s.ended_at) : new Date();
  let sec = (end - new Date(s.started_at)) / 1000;
  for (const b of s.breaks || []) {
    if (b.start && b.end) sec -= (new Date(b.end) - new Date(b.start)) / 1000;
  }
  return Math.max(0, Math.round(sec));
}

function WorkerTimeBlock({ name, role, roleClass, session, testid }) {
  const breaks = session.breaks || [];
  return (
    <div className="rounded-lg border border-border p-3" data-testid={testid}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-bold">{name}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${roleClass}`}>{role}</span>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-muted-foreground">
            <th className="pb-1 font-semibold">Started</th>
            <th className="pb-1 font-semibold">Ended</th>
            <th className="pb-1 text-right font-semibold">Worked</th>
          </tr>
        </thead>
        <tbody>
          <tr className="font-medium">
            <td className="py-0.5">{fmtTime(session.started_at)}</td>
            <td className="py-0.5">{session.ended_at ? fmtTime(session.ended_at) : <span className="text-emerald-600">In progress</span>}</td>
            <td className="py-0.5 text-right tabular-nums">{fmtDur(workerNetSeconds(session))}</td>
          </tr>
        </tbody>
      </table>
      {breaks.length > 0 && (
        <table className="mt-2 w-full border-t border-dashed border-border pt-2 text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="pb-1 font-semibold">Break</th>
              <th className="pb-1 font-semibold">Start</th>
              <th className="pb-1 font-semibold">End</th>
            </tr>
          </thead>
          <tbody>
            {breaks.map((b, i) => (
              <tr key={b.start || `brk-${i}`}>
                <td className="py-0.5 capitalize">{b.type || "break"}</td>
                <td className="py-0.5">{fmtTime(b.start)}</td>
                <td className="py-0.5">{b.end ? fmtTime(b.end) : <span className="text-amber-600">ongoing</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TimeDetailsDialog({ clean, onClose }) {
  if (!clean) return null;
  const workers = [];
  if (clean.session?.started_at) {
    workers.push({
      name: clean.claimed_by_name || "Cleaner",
      role: "Cleaner",
      roleClass: "bg-emerald-100 text-emerald-700",
      session: clean.session,
    });
  }
  for (const a of clean.assist_sessions || []) {
    if (!a.started_at) continue;
    workers.push({
      name: a.cleaner_name || "Worker",
      role: a.is_manager ? "Manager (record only)" : "Assist",
      roleClass: a.is_manager ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-700",
      session: a,
    });
  }
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="time-details-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Time details — Room {clean.room_number}</DialogTitle>
          <DialogDescription>Start / end and break times per person. View only.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {workers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No recorded work sessions yet.</p>
          ) : (
            workers.map((w, i) => (
              <WorkerTimeBlock
                key={`${w.role}-${w.name}-${i}`}
                name={w.name}
                role={w.role}
                roleClass={w.roleClass}
                session={w.session}
                testid={`time-block-${clean.room_number}-${i}`}
              />
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="time-details-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditTimeDialog({ clean, rooms, cleaners, onClose, onSave, onMove, pending }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [cleaner, setCleaner] = useState("");
  const [moveTo, setMoveTo] = useState("");
  const [moveStrip, setMoveStrip] = useState(true);
  useEffect(() => {
    if (clean) {
      setStart(toLocalInput(clean.session?.started_at));
      setEnd(toLocalInput(clean.session?.ended_at));
      setCleaner(clean.claimed_by || "");
      setMoveTo("");
      setMoveStrip(true);
    }
  }, [clean]);
  if (!clean) return null;
  const targets = (rooms || []).filter((r) => r.id && r.id !== clean.id);
  const isStripped = !!(clean.strip && clean.strip.done);
  const cleanerList = cleaners || [];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-time-dialog">
        <DialogHeader>
          <DialogTitle>Set cleaner &amp; time — Room {clean.room_number}</DialogTitle>
          <DialogDescription>
            Choose who did this task and correct their start/finish times — this works even after it's completed. Or move the time to the room they meant to clean.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Cleaner</Label>
            <select
              value={cleaner}
              onChange={(e) => setCleaner(e.target.value)}
              data-testid="edit-time-cleaner"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">Choose a cleaner…</option>
              {cleanerList.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start</Label>
              <Input
                type="datetime-local"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                data-testid="edit-time-start"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Finish</Label>
              <Input
                type="datetime-local"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                data-testid="edit-time-end"
              />
            </div>
          </div>
          <Button
            disabled={pending || !cleaner}
            onClick={() => onSave({ cleaner_id: cleaner, started_at: fromLocalInput(start), ended_at: fromLocalInput(end) })}
            data-testid="edit-time-save"
            className="k-press w-full gap-2"
          >
            <Clock className="h-4 w-4" /> Save cleaner &amp; time
          </Button>

          {clean.claimed_by && (
          <div className="space-y-1.5 border-t border-dashed border-border pt-4">
            <Label>Move this time to the correct room</Label>
            <select
              value={moveTo}
              onChange={(e) => setMoveTo(e.target.value)}
              data-testid="move-time-select"
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">Choose a room…</option>
              {targets.map((r) => (
                <option key={r.id} value={r.id}>
                  {`Room ${r.room_number} ${r.claimed_by_name ? `(with ${r.claimed_by_name})` : "(unassigned)"}`}
                </option>
              ))}
            </select>
            {isStripped && (
              <label
                className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs font-medium text-amber-800"
                data-testid="move-strip-toggle"
              >
                <Checkbox
                  checked={moveStrip}
                  onCheckedChange={(v) => setMoveStrip(!!v)}
                  data-testid="move-strip-checkbox"
                  className="mt-0.5"
                />
                <span>
                  Also move the <strong>stripping record</strong>
                  {clean.strip?.by_name ? ` (stripped by ${clean.strip.by_name})` : ""} to the new room.
                </span>
              </label>
            )}
            <Button
              variant="outline"
              disabled={pending || !moveTo}
              onClick={() => onMove(moveTo, isStripped ? moveStrip : false)}
              data-testid="move-time-confirm"
              className="k-press mt-2 w-full gap-2"
            >
              <RefreshCw className="h-4 w-4" /> Move time to selected room
            </Button>
          </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="edit-time-cancel">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SpecialDialog({ clean, library, onClose, onAdd }) {
  const [name, setName] = useState("");
  const [minutes, setMinutes] = useState(5);
  if (!clean) return null;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="special-dialog">
        <DialogHeader>
          <DialogTitle>Special attention — Room {clean.room_number}</DialogTitle>
          <DialogDescription>
            Add an optional extra for the cleaner to pay special attention to today. It appears highlighted on their checklist.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="max-h-52 space-y-3 overflow-y-auto pr-1">
            {Object.entries(
              library.reduce((acc, t) => {
                (acc[t.area || "Other"] = acc[t.area || "Other"] || []).push(t);
                return acc;
              }, {})
            ).map(([area, items]) => (
              <div key={area}>
                <div className="label-caps mb-1 text-muted-foreground">{area}</div>
                <div className="flex flex-wrap gap-1.5">
                  {items.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={`special-lib-${t.name}`}
                      onClick={() => onAdd(t.name, t.minutes)}
                      className="k-press rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
                    >
                      {t.name} · {t.minutes}m
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-border pt-3">
            <div className="label-caps mb-1.5 text-muted-foreground">Or add your own</div>
            <div className="flex gap-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Wipe down patio doors"
                data-testid="special-custom-name"
              />
              <Input
                type="number"
                min="1"
                value={minutes}
                onChange={(e) => setMinutes(Number(e.target.value) || 0)}
                className="w-20"
                data-testid="special-custom-minutes"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            disabled={!name.trim()}
            onClick={() => onAdd(name.trim(), minutes)}
            data-testid="special-add-btn"
            className="gap-2 bg-amber-500 hover:bg-amber-600"
          >
            <Star className="h-4 w-4" /> Add special task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatTile({ label, value, tone }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs ${tone ? `status-${tone}` : "bg-card"}`}>
      <span className="opacity-75">{label}</span>
      <span className="font-display text-sm font-extrabold">{value}</span>
    </span>
  );
}

function SortControl({ sortBy, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const opts = [
    { key: "room", label: "Room number" },
    { key: "type", label: "Task type" },
  ];
  const current = opts.find((o) => o.key === sortBy) || opts[0];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="sort-control"
          className="k-press inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-sm font-medium text-muted-foreground hover:bg-secondary"
        >
          <ArrowUpDown className="h-3.5 w-3.5" />
          {t("Sort")}: {t(current.label)}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-1" data-testid="sort-menu">
        {opts.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => { setOpen(false); onChange(o.key); }}
            data-testid={`sort-${o.key}`}
            className={`k-press flex w-full items-center rounded px-2 py-1.5 text-left text-xs font-semibold ${
              sortBy === o.key ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"
            }`}
          >
            {t(o.label)}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}


function StateSelector({ roomNumber, current, priority, onSetState, allowClear, fixedWidth }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const opts = [
    { key: "departing", label: "Departing" },
    { key: "in_house", label: "In-house" },
    { key: "due", label: "Due · Priority" },
  ];
  // Merged status control: shows the current state in its own colour (matching StatusBadge)
  // AND acts as the change-status dropdown — so there's no separate coloured badge + button.
  const META = {
    departing: { cls: "status-departing", Icon: DoorOpen, label: "Departing" },
    in_house: { cls: "status-in_house", Icon: BedDouble, label: "In-house" },
    due: { cls: "status-due", Icon: AlertTriangle, label: "Due · Priority" },
  };
  const currentMeta = priority
    ? { cls: "status-priority", Icon: LogOut, label: "Due — Priority" }
    : META[current];
  const pick = (key) => { setOpen(false); if (key !== current) onSetState(key); };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`state-selector-${roomNumber}`}
          className={`k-press inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-semibold ${
            fixedWidth ? "w-36 shrink-0 justify-between" : ""
          } ${currentMeta ? currentMeta.cls : "border-border text-muted-foreground hover:bg-secondary"}`}
        >
          <span className="inline-flex items-center gap-1.5 truncate">
            {currentMeta && <currentMeta.Icon className="h-3 w-3 shrink-0" />}
            {currentMeta ? t(currentMeta.label) : t("Set status")}
          </span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-44 p-1" data-testid={`state-menu-${roomNumber}`}>
        {opts.map((o) => (
          <button
            key={o.key}
            type="button"
            onClick={() => pick(o.key)}
            data-testid={`set-${o.key === "in_house" ? "inhouse" : o.key}-${roomNumber}`}
            className={`k-press flex w-full items-center rounded px-2 py-1.5 text-left text-xs font-semibold ${
              current === o.key ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"
            }`}
          >
            {t(o.label)}
          </button>
        ))}
        {allowClear && (
          <button
            type="button"
            onClick={() => { setOpen(false); onSetState("none"); }}
            data-testid={`set-none-${roomNumber}`}
            className="k-press mt-0.5 flex w-full items-center rounded border-t border-border px-2 py-1.5 text-left text-xs font-semibold text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            title="Remove this room from today's clean list"
          >
            {t("Clear")}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}


function CheckoutTimeControl({ clean, defaultCheckout, onSetCheckout }) {
  const [open, setOpen] = useState(false);
  const effective = clean.checkout_time || defaultCheckout || "10:00";
  const isLate = !!clean.checkout_time && clean.checkout_time > (defaultCheckout || "10:00");
  const [val, setVal] = useState(effective);
  useEffect(() => { setVal(effective); }, [effective]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`checkout-time-${clean.room_number}`}
          className={`k-press inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold ${isLate ? "border-orange-300 bg-orange-50 text-orange-700" : "border-dashed border-border text-muted-foreground hover:bg-secondary"}`}
          title="Expected checkout time (auto key-return fires at this time)"
        >
          <Clock className="h-3 w-3" /> Checkout {effective}{isLate ? " · late" : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 space-y-3" data-testid={`checkout-popover-${clean.room_number}`}>
        <div>
          <div className="text-sm font-semibold">Expected checkout</div>
          <p className="text-xs text-muted-foreground">Auto key-return fires for this room at this NZ time. Default is {defaultCheckout || "10:00"}.</p>
        </div>
        <Input
          type="time"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          data-testid={`checkout-input-${clean.room_number}`}
        />
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => { onSetCheckout(""); setOpen(false); }}
            data-testid={`checkout-reset-${clean.room_number}`}
            className="k-press text-xs font-semibold text-muted-foreground underline hover:text-foreground"
          >
            Reset to default
          </button>
          <Button
            size="sm"
            data-testid={`checkout-save-${clean.room_number}`}
            onClick={() => { onSetCheckout(val); setOpen(false); }}
            className="k-press h-8"
          >
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CompactMore({ clean, isExtra, isGeneral, needsStrip, stripped, isPending, hasTimes, canSpecial, onEditGuest, stay, onStay, onEditTime, onViewTimes, onAddSpecial, onStrip, onDefer }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isRoom = !isExtra && !isGeneral;
  const Item = ({ onClick, testid, icon: Icon, label }) => (
    <button
      type="button"
      onClick={() => { setOpen(false); onClick(); }}
      data-testid={testid}
      className="k-press flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs font-semibold text-foreground hover:bg-secondary"
    >
      <Icon className="h-3.5 w-3.5 shrink-0" /> {label}
    </button>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`more-${clean.room_number}`}
          className="k-press inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-secondary"
          title={t("More")}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-52 p-1" data-testid={`more-menu-${clean.room_number}`}>
        {isRoom && onEditGuest && clean.id && <Item onClick={onEditGuest} testid={`edit-guest-${clean.room_number}`} icon={Pencil} label={t("Edit guest")} />}
        {isRoom && onStay && <Item onClick={onStay} testid={`long-stay-${clean.room_number}`} icon={BedDouble} label={stay ? t("Long stay · until {{date}}", { date: fmtDMY(stay.check_out) }) : t("Long stay")} />}
        {clean.id && onEditTime && <Item onClick={onEditTime} testid={`edit-time-${clean.room_number}`} icon={Clock} label={t("Set cleaner & time")} />}
        {hasTimes && onViewTimes && <Item onClick={onViewTimes} testid={`view-times-${clean.room_number}`} icon={ListChecks} label={t("View times")} />}
        {needsStrip && isPending && !stripped && <Item onClick={() => onStrip(true)} testid={`mark-stripped-${clean.room_number}`} icon={Sparkles} label={t("Mark stripped")} />}
        {needsStrip && isPending && stripped && <Item onClick={() => onStrip(false)} testid={`undo-strip-${clean.room_number}`} icon={RotateCcw} label={t("Undo strip")} />}
        {canSpecial && onAddSpecial && <Item onClick={onAddSpecial} testid={`add-special-${clean.room_number}`} icon={Star} label={t("Add special")} />}
        {isGeneral && onDefer && <Item onClick={onDefer} testid={`defer-general-${clean.room_number}`} icon={CalendarClock} label={t("Defer")} />}
      </PopoverContent>
    </Popover>
  );
}


function CleanRow({ clean, cleaners, defaultCheckout, compact, onAssign, onAddSpecial, onRemoveSpecial, onDefer, onSkip, onChangeStatus, onUndoDnd, onSetState, onEarlyCheckout, onRemoveEarlyCheckout, onStrip, onKeyReturn, onSetCheckout, onEditTime, onViewTimes, onFixStrip, onEditGuest, stay, onStay }) {
  const { t } = useTranslation();
  const isExtra = clean.is_extra;
  const isGeneral = clean.is_general;
  const idle = clean.needs_clean === false && !isExtra && !isGeneral;
  const specials = (clean.checklist || []).filter((i) => i.special);
  const canSpecial = !isExtra && !isGeneral && !!clean.id;
  const isPending = (clean.session?.status || "pending") === "pending";
  const canChangeStatus =
    !isExtra && !isGeneral && !!clean.id && clean.needs_clean !== false;
  const currentStatus = clean.status === "in_house" ? "in_house" : "departing";
  const needsStrip = !isExtra && !isGeneral && clean.clean_type === "full" && clean.needs_clean !== false && !!clean.id;
  const stripped = !!(clean.strip && clean.strip.done);
  const guests = clean.guests || [];
  const paxNum = (s) => { const m = String(s ?? "").match(/\d+/); return m ? m[0] : ""; };

  if (idle) {
    return (
      <Card
        data-testid={`clean-row-${clean.room_number}`}
        className={`relative flex flex-col gap-3 rounded-md border border-dashed bg-muted/30 opacity-75 md:flex-row md:items-center md:gap-4 ${compact ? "p-2" : "p-4"}`}
      >
        <div className="flex min-w-[130px] items-center gap-3">
          <div className="font-display text-xl font-extrabold text-muted-foreground">{clean.room_number}</div>
          <div className="text-xs text-muted-foreground">{clean.room_type}</div>
        </div>
        <div className="flex flex-1 items-center gap-1.5 text-sm text-muted-foreground">
          <MoonStar className="h-4 w-4" />
          No clean scheduled today
        </div>
        {onStay && (
          <button
            type="button"
            onClick={onStay}
            data-testid={`long-stay-${clean.room_number}`}
            className={`k-press inline-flex items-center gap-1 text-[11px] font-semibold ${stay ? "text-indigo-700" : "text-muted-foreground hover:text-foreground"}`}
            title={stay ? t("Long stay until {{date}} — edit in-stay tasks", { date: fmtDMY(stay.check_out) }) : t("Set up a long stay with recurring in-stay tasks")}
          >
            <BedDouble className="h-3 w-3" /> {stay ? t("Long stay · until {{date}}", { date: fmtDMY(stay.check_out) }) : t("Long stay")}
          </button>
        )}
        <StateSelector
          roomNumber={clean.room_number}
          current="none"
          onSetState={onSetState}
        />
      </Card>
    );
  }

  const meta = SESSION_META[clean.session?.status || "pending"];
  const MetaIcon = meta.Icon;
  const spinning = clean.session?.status === "in_progress";
  const assignedIds = clean.assigned_cleaner_ids || [];
  const hasTimes = !!(clean.session?.started_at) || (clean.assist_sessions || []).some((a) => a.started_at);
  // Helpers CURRENTLY assisting (running or on a break) — a live team-load count, distinct
  // from `assist_names` which also includes helpers who already finished their assist.
  const liveHelpers = (clean.assist_sessions || []).filter((a) => a.status === "in_progress" || a.status === "paused").length;
  // If a serviced (in-house) room is set to Departing after being cleaned, offer an
  // "Early Checkout" re-clean that keeps the original service record.
  const handleSetState = (state) => {
    if (state === "departing" && clean.clean_type === "service" && hasTimes && !clean.is_reclean && onEarlyCheckout) {
      onEarlyCheckout();
    } else {
      onSetState(state);
    }
  };

  if (compact) {
    const g0 = guests[0];
    const a0 = g0 ? paxNum(g0.adults) : "";
    const c0 = g0 ? paxNum(g0.children) : "";
    return (
      <Card
        data-testid={`clean-row-${clean.room_number}`}
        className={`relative flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-md border px-3 py-1.5 text-sm ${
          clean.is_priority ? "ring-priority" : isExtra || isGeneral ? "" : `ring-${clean.status}`
        } ${needsStrip && stripped && isPending ? "card-stripped" : ""}`}
      >
        {isExtra || isGeneral ? (
          <>
            <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${isExtra ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
              {isExtra ? t("Extra task") : t("Scheduled task")}
            </span>
            <span className="min-w-0 max-w-[280px] truncate text-sm font-semibold" title={clean.room_number}>{clean.room_number}</span>
          </>
        ) : (
          <>
            <span className="w-10 shrink-0 font-display text-lg font-extrabold leading-none">{clean.room_number}</span>
            <span className="hidden w-28 shrink-0 truncate text-[11px] text-muted-foreground lg:inline">{clean.room_type}</span>
          </>
        )}

        {!isExtra && !isGeneral && (
          canChangeStatus ? (
            <StateSelector roomNumber={clean.room_number} current={clean.is_priority ? "due" : currentStatus} priority={clean.is_priority} onSetState={handleSetState} allowClear={isPending} fixedWidth />
          ) : (
            <div className="w-36 shrink-0">{clean.is_priority ? <PriorityBadge /> : <StatusBadge status={clean.status} />}</div>
          )
        )}

        {!isExtra && !isGeneral && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`guest-info-${clean.room_number}`}>
            <User className="h-3 w-3 shrink-0" />
            {g0 ? (
              <>
                <span className="max-w-[130px] truncate font-medium text-foreground" title={g0.name}>{g0.name || "—"}</span>
                {(a0 || c0) && <span className="shrink-0">· {a0 || 0}A{c0 && Number(c0) > 0 ? ` ${c0}C` : ""}</span>}
                {guests.length > 1 && <span className="shrink-0">+{guests.length - 1}</span>}
              </>
            ) : (
              <span className="italic text-muted-foreground/60">{t("No guest info")}</span>
            )}
          </span>
        )}

        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" /> {fmtMins(clean.est_minutes)}
        </span>

        <span className={`inline-flex items-center gap-1 text-xs font-medium ${meta.cls}`}>
          <MetaIcon className={`h-3.5 w-3.5 ${spinning ? "animate-spin" : ""}`} />
          {meta.label}
          {clean.claimed_by_name && <span className="text-muted-foreground">· {clean.claimed_by_name}</span>}
          {liveHelpers > 0 && (
            <span className="font-bold text-teal-700" data-testid={`live-helpers-${clean.room_number}`}>· {t("{{n}} helping", { n: liveHelpers })}</span>
          )}
        </span>

        <div className="ml-auto flex items-center gap-2">
          {!clean.claimed_by && !isExtra && (
            <AssignPopover clean={clean} cleaners={cleaners} assignedIds={assignedIds} onAssign={onAssign} />
          )}
          {isGeneral && clean.task_def_id && (clean.session?.status || "pending") !== "completed" && onSkip && (
            <button
              type="button"
              onClick={onSkip}
              data-testid={`skip-general-${clean.room_number}`}
              title={t("Remove today")}
              className="k-press inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-dashed border-border px-1.5 text-[11px] font-semibold text-muted-foreground hover:bg-secondary hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" /> <span className="hidden xl:inline">{t("Remove today")}</span>
            </button>
          )}
          <CompactMore
            clean={clean}
            isExtra={isExtra}
            isGeneral={isGeneral}
            needsStrip={needsStrip}
            stripped={stripped}
            isPending={isPending}
            hasTimes={hasTimes}
            canSpecial={canSpecial}
            onEditGuest={onEditGuest}
            stay={stay}
            onStay={onStay}
            onEditTime={onEditTime}
            onViewTimes={onViewTimes}
            onAddSpecial={onAddSpecial}
            onStrip={onStrip}
            onDefer={onDefer}
          />
        </div>
      </Card>
    );
  }

  return (
    <Card
      data-testid={`clean-row-${clean.room_number}`}
      className={`relative flex flex-col gap-3 rounded-md border md:flex-row md:items-center md:gap-4 ${compact ? "p-2" : "p-4"} ${
        clean.is_priority ? "ring-priority" : isExtra || isGeneral ? "" : `ring-${clean.status}`
      } ${needsStrip && stripped && isPending ? "card-stripped" : ""}`}
    >
      <div className="flex min-w-[130px] max-w-[280px] items-center gap-3">
        {isExtra || isGeneral ? (
          <div className="line-clamp-2 text-sm font-semibold leading-tight" title={clean.room_number}>{clean.room_number}</div>
        ) : (
          <>
            <div className="font-display text-xl font-extrabold">{clean.room_number}</div>
            <div className="text-xs text-muted-foreground">{clean.room_type}</div>
          </>
        )}
      </div>

      {!isExtra && !isGeneral && (
        <div className="flex min-w-[150px] flex-col gap-0.5" data-testid={`guest-info-${clean.room_number}`}>
          {guests.length > 0 ? (
            guests.map((g, i) => {
              const a = paxNum(g.adults);
              const ch = paxNum(g.children);
              return (
                <div key={`${g.name || "guest"}-${i}`} className="flex items-center gap-1 text-xs text-muted-foreground">
                  <User className="h-3 w-3 shrink-0" />
                  <span className="truncate font-medium text-foreground" title={g.name}>{g.name || "—"}</span>
                  {(a || ch) && (
                    <span className="shrink-0">· {a || 0}A{ch && Number(ch) > 0 ? ` ${ch}C` : ""}</span>
                  )}
                </div>
              );
            })
          ) : (
            <span className="text-xs italic text-muted-foreground/60">{t("No guest info")}</span>
          )}
          {onEditGuest && clean.id && (
            <button
              type="button"
              onClick={onEditGuest}
              data-testid={`edit-guest-${clean.room_number}`}
              className="k-press inline-flex items-center gap-1 self-start text-[11px] font-semibold text-muted-foreground hover:text-foreground"
            >
              <Pencil className="h-3 w-3" /> {t("Edit guest")}
            </button>
          )}
          {onStay && (
            <button
              type="button"
              onClick={onStay}
              data-testid={`long-stay-${clean.room_number}`}
              className={`k-press inline-flex items-center gap-1 self-start text-[11px] font-semibold ${stay ? "text-indigo-700" : "text-muted-foreground hover:text-foreground"}`}
              title={stay ? t("Long stay until {{date}} — edit in-stay tasks", { date: fmtDMY(stay.check_out) }) : t("Set up a long stay with recurring in-stay tasks")}
            >
              <BedDouble className="h-3 w-3" /> {stay ? t("Long stay · until {{date}}", { date: fmtDMY(stay.check_out) }) : t("Long stay")}
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {isExtra ? (
          <span className="inline-flex items-center gap-1.5 rounded-md border border-purple-200 bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">
            Extra task
          </span>
        ) : isGeneral ? (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
              Scheduled task
            </span>
            {clean.task_def_id && (clean.session?.status || "pending") === "pending" && (
              <button
                type="button"
                onClick={onDefer}
                data-testid={`defer-general-${clean.room_number}`}
                className="k-press inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-0.5 text-xs font-semibold text-muted-foreground hover:bg-secondary"
              >
                <CalendarClock className="h-3 w-3" /> Defer
              </button>
            )}
            {clean.task_def_id && (clean.session?.status || "pending") !== "completed" && onSkip && (
              <button
                type="button"
                onClick={onSkip}
                data-testid={`skip-general-${clean.room_number}`}
                className="k-press inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-0.5 text-xs font-semibold text-muted-foreground hover:bg-secondary hover:text-destructive"
              >
                <Trash2 className="h-3 w-3" /> Remove today
              </button>
            )}
          </div>
        ) : (
          <>
            {clean.early_checkout && (
              <span
                data-testid={`early-checkout-badge-${clean.room_number}`}
                className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800"
                title="Guest checked out early after this room was serviced — full clean needed"
              >
                <LogOut className="h-3 w-3" /> {t("Early Checkout")}
              </span>
            )}
            {clean.early_checkout && isPending && onRemoveEarlyCheckout && (
              <button
                type="button"
                onClick={onRemoveEarlyCheckout}
                data-testid={`remove-early-checkout-${clean.room_number}`}
                className="k-press inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-0.5 text-xs font-semibold text-muted-foreground hover:bg-secondary hover:text-destructive"
                title="Added by mistake? Remove this Early Checkout clean"
              >
                <Trash2 className="h-3 w-3" /> {t("Remove")}
              </button>
            )}
            {canChangeStatus ? (
              <StateSelector
                roomNumber={clean.room_number}
                current={clean.is_priority ? "due" : currentStatus}
                priority={clean.is_priority}
                onSetState={handleSetState}
                allowClear={isPending}
              />
            ) : (
              clean.is_priority ? <PriorityBadge /> : <StatusBadge status={clean.status} />
            )}
            <span className="hidden text-xs text-muted-foreground sm:inline">{t(STATUS_DESC[clean.status])}</span>
            {clean.status_undelivered && (
              <span
                data-testid={`undelivered-badge-${clean.room_number}`}
                className="inline-flex items-center gap-1 rounded-md border border-orange-300 bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700"
                title="Status changed — not yet synced to the cleaner's app"
              >
                <AlertTriangle className="h-3 w-3" /> Undelivered
              </span>
            )}
            {clean.reopened && (clean.session?.status || "pending") !== "completed" && (
              <span
                data-testid={`reopened-badge-${clean.room_number}`}
                className="inline-flex items-center gap-1 rounded-md border border-rose-300 bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700"
                title={`Reopened by ${clean.reopened_by || "a cleaner"} — needs another cleaner to finish`}
              >
                <AlertTriangle className="h-3 w-3" /> Reopened
              </span>
            )}
            {clean.dnd && (
              <span
                data-testid={`dnd-badge-${clean.room_number}`}
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600"
                title={`Do Not Disturb — closed by ${clean.dnd_by_name || "a cleaner"}, no time recorded`}
              >
                <MoonStar className="h-3 w-3" /> DND · not cleaned
                <button
                  type="button"
                  data-testid={`undo-dnd-${clean.room_number}`}
                  onClick={(e) => { e.stopPropagation(); onUndoDnd(); }}
                  className="ml-1 inline-flex items-center gap-0.5 rounded border border-slate-400 bg-white px-1.5 py-0 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                  title="Undo Do Not Disturb — put the room back on the board"
                >
                  <RotateCcw className="h-3 w-3" /> Undo
                </button>
              </span>
            )}
            {needsStrip && !stripped && clean.checkout_time && defaultCheckout && clean.checkout_time > defaultCheckout && (
              <span
                data-testid={`late-checkout-badge-${clean.room_number}`}
                className="inline-flex items-center gap-1 rounded-md border border-orange-300 bg-orange-50 px-2 py-0.5 text-xs font-semibold text-orange-700"
                title="Late checkout — don't strip until this time"
              >
                <Clock className="h-3 w-3" /> Late checkout {clean.checkout_time}
              </span>
            )}
            {needsStrip && (
              stripped ? (
                <span
                  data-testid={`strip-status-${clean.room_number}`}
                  className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"
                  title={`Stripped by ${clean.strip?.by_name || "—"}`}
                >
                  <Sparkles className="h-3 w-3" /> Stripped · {clean.strip?.by_name || "—"}
                  {isPending && onFixStrip && (
                    <button
                      type="button"
                      onClick={onFixStrip}
                      data-testid={`fix-strip-${clean.room_number}`}
                      className="k-press ml-1 underline hover:text-emerald-900"
                      title="Correct who stripped this room"
                    >
                      Fix
                    </button>
                  )}
                  {isPending && (
                    <button
                      type="button"
                      onClick={() => onStrip(false)}
                      data-testid={`undo-strip-${clean.room_number}`}
                      className="k-press ml-1 underline hover:text-emerald-900"
                    >
                      Undo
                    </button>
                  )}
                </span>
              ) : isPending ? (
                clean.key_returned ? (
                  <span className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onKeyReturn(false)}
                      data-testid={`undo-key-return-${clean.room_number}`}
                      className="k-press inline-flex items-center gap-1 rounded-md border border-dashed border-sky-300 bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700 hover:bg-sky-100"
                      title="Undo key return"
                    >
                      <KeyRound className="h-3 w-3" /> Key back
                    </button>
                    <button
                      type="button"
                      onClick={() => onStrip(true)}
                      data-testid={`mark-stripped-${clean.room_number}`}
                      className="k-press inline-flex items-center gap-1 rounded-md border border-dashed border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                    >
                      <Sparkles className="h-3 w-3" /> Mark stripped
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onKeyReturn(true)}
                    data-testid={`key-returned-${clean.room_number}`}
                    className="k-press inline-flex items-center gap-1 rounded-md border border-dashed border-sky-300 bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700 hover:bg-sky-100"
                    title="Guest hasn't left yet — mark the key returned before stripping"
                  >
                    <KeyRound className="h-3 w-3" /> Key Returned
                  </button>
                )
              ) : null
            )}
            {needsStrip && (
              <CheckoutTimeControl clean={clean} defaultCheckout={defaultCheckout} onSetCheckout={onSetCheckout} />
            )}
          </>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-sm md:ml-auto">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">Est {fmtMins(clean.est_minutes)}</span>
      </div>

      <div className={`flex items-center gap-1.5 text-sm font-medium ${meta.cls} min-w-[150px]`}>
        <MetaIcon className={`h-4 w-4 ${spinning ? "animate-spin" : ""}`} />
        <span>{meta.label}</span>
        {clean.claimed_by_name && (
          <span className="text-muted-foreground">· {clean.claimed_by_name}</span>
        )}
        {(clean.total_actual_seconds || clean.actual_seconds) > 0 && (
          <span className="text-muted-foreground">· {fmtDur(clean.total_actual_seconds || clean.actual_seconds)}</span>
        )}
        {liveHelpers > 0 && (
          <span
            data-testid={`live-helpers-${clean.room_number}`}
            className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-bold text-teal-700"
            title={t("{{n}} currently assisting this room", { n: liveHelpers })}
          >
            <HandHelping className="h-3 w-3" /> {t("{{n}} helping", { n: liveHelpers })}
          </span>
        )}
      </div>

      <div className="min-w-[190px]">
        {isExtra ? (
          <span className="text-xs text-muted-foreground">Added by {clean.claimed_by_name || "cleaner"}</span>
        ) : clean.claimed_by ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5 rounded-md border border-border bg-secondary/50 px-3 py-2 text-xs font-medium">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              Claimed by {clean.claimed_by_name}
            </div>
            {(clean.assist_names || []).length > 0 && (
              <span className="px-1 text-[11px] font-medium text-indigo-600" data-testid={`helpers-${clean.room_number}`}>
                Helped by {clean.assist_names.join(", ")}
              </span>
            )}
            <div className="flex flex-wrap gap-x-3">
              <button
                type="button"
                onClick={onEditTime}
                data-testid={`edit-time-${clean.room_number}`}
                className="k-press inline-flex items-center gap-1 self-start px-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
              >
                <Clock className="h-3 w-3" /> Set cleaner &amp; time
              </button>
              {hasTimes && (
                <button
                  type="button"
                  onClick={onViewTimes}
                  data-testid={`view-times-${clean.room_number}`}
                  className="k-press inline-flex items-center gap-1 self-start px-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
                >
                  <ListChecks className="h-3 w-3" /> View times
                </button>
              )}
            </div>
          </div>
        ) : hasTimes ? (
          <div className="flex flex-col gap-1">
            <AssignPopover clean={clean} cleaners={cleaners} assignedIds={assignedIds} onAssign={onAssign} />
            <div className="flex flex-wrap gap-x-3">
              <button
                type="button"
                onClick={onEditTime}
                data-testid={`edit-time-${clean.room_number}`}
                className="k-press inline-flex items-center gap-1 self-start px-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
              >
                <Clock className="h-3 w-3" /> Set cleaner &amp; time
              </button>
              <button
                type="button"
                onClick={onViewTimes}
                data-testid={`view-times-${clean.room_number}`}
                className="k-press inline-flex items-center gap-1 self-start px-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
              >
                <ListChecks className="h-3 w-3" /> View times
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <AssignPopover clean={clean} cleaners={cleaners} assignedIds={assignedIds} onAssign={onAssign} />
            {clean.id && (
              <button
                type="button"
                onClick={onEditTime}
                data-testid={`edit-time-${clean.room_number}`}
                className="k-press inline-flex items-center gap-1 self-start px-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
              >
                <Clock className="h-3 w-3" /> Set cleaner &amp; time
              </button>
            )}
          </div>
        )}
      </div>

      {canSpecial && (
        <div className="flex w-full flex-wrap items-center gap-1.5 border-t border-dashed border-border pt-2.5 md:basis-full">
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase text-amber-600">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" /> Special
          </span>
          {specials.map((s) => (
            <span
              key={s.id}
              data-testid={`special-chip-${clean.room_number}-${s.name}`}
              className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800"
            >
              {s.name} · {s.est}m
              <button
                type="button"
                onClick={() => onRemoveSpecial(s.id)}
                data-testid={`remove-special-${clean.room_number}-${s.name}`}
                className="k-press ml-0.5 rounded-full hover:bg-amber-200"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={onAddSpecial}
            data-testid={`add-special-${clean.room_number}`}
            className="k-press inline-flex items-center gap-1 rounded-full border border-dashed border-amber-300 px-2 py-0.5 text-xs font-semibold text-amber-700 hover:bg-amber-50"
          >
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
      )}
    </Card>
  );
}

function AssignPopover({ clean, cleaners, assignedIds, onAssign }) {
  const allSelected = cleaners.length > 0 && assignedIds.length === cleaners.length;
  const toggle = (id) => {
    const next = assignedIds.includes(id)
      ? assignedIds.filter((x) => x !== id)
      : [...assignedIds, id];
    onAssign({ cleanId: clean.id, cleanerIds: next });
  };
  const toggleAll = () => {
    onAssign({ cleanId: clean.id, cleanerIds: allSelected ? [] : cleaners.map((c) => c.id) });
  };
  const label =
    assignedIds.length === 0
      ? "Assign cleaners"
      : allSelected
      ? "All cleaners"
      : `${assignedIds.length} assigned`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid={`assign-select-${clean.room_number}`}
          className="h-9 w-full justify-between gap-2 font-medium"
        >
          <span className="flex items-center gap-2">
            <Users className="h-3.5 w-3.5" />
            {label}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <div
          role="button"
          tabIndex={0}
          onClick={toggleAll}
          data-testid={`assign-all-${clean.room_number}`}
          className="mb-1 flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm font-semibold hover:bg-secondary"
        >
          <Checkbox checked={allSelected} className="pointer-events-none" />
          All cleaners
        </div>
        <div className="h-px bg-border" />
        <div className="mt-1 max-h-60 space-y-0.5 overflow-y-auto">
          {cleaners.length === 0 && (
            <p className="px-2 py-2 text-xs text-muted-foreground">No cleaners yet.</p>
          )}
          {cleaners.map((cl) => (
            <div
              key={cl.id}
              role="button"
              tabIndex={0}
              onClick={() => toggle(cl.id)}
              data-testid={`assign-cleaner-${clean.room_number}-${cl.id}`}
              className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-secondary"
            >
              <Checkbox checked={assignedIds.includes(cl.id)} className="pointer-events-none" />
              {cl.name}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function EmptyState({ onUpload }) {
  return (
    <Card className="flex flex-col items-center justify-center gap-4 rounded-md border border-dashed py-20 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary">
        <Upload className="h-6 w-6 text-muted-foreground" />
      </div>
      <div>
        <div className="font-display text-lg font-bold">No cleaning list for this day</div>
        <p className="mt-1 text-sm text-muted-foreground">
          Upload the morning housekeeping CSV to generate today's rooms.
        </p>
      </div>
      <Button onClick={onUpload} data-testid="empty-upload-btn" className="gap-2">
        <Upload className="h-4 w-4" /> Upload CSV
      </Button>
    </Card>
  );
}
